import axios, { AxiosResponse } from 'axios';
import { LoginData, SocialAuthResponse, Token, User, UserCreate } from '../types';

// Safely import Capacitor - it may not be available in all environments
let Capacitor: any = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  Capacitor = require('@capacitor/core').Capacitor;
} catch (error) {
  // Capacitor not available, will use web mode
}

// Helper to check if running on native platform
const isNativePlatform = (): boolean => {
  try {
    return Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform();
  } catch (error) {
    return false;
  }
};

// API URL configuration for different environments
const getApiUrl = (): string => {
  // For Capacitor (native mobile), always use absolute URLs
  if (isNativePlatform()) {
    // Use production API for now, or you can configure per environment
    const nativeUrl =
      process.env.REACT_APP_API_URL || 'https://clout-backend-875110446242.us-central1.run.app';
    // eslint-disable-next-line no-console
    console.log('[API Config] Native platform detected, using API URL:', nativeUrl);
    return nativeUrl;
  }

  // For web in production
  if (process.env.NODE_ENV === 'production') {
    const prodUrl = process.env.REACT_APP_API_URL || '';
    if (!prodUrl) {
      // eslint-disable-next-line no-console
      console.error(
        '[API Config] CRITICAL: REACT_APP_API_URL not set in production! Using empty string (relative URLs)'
      );
    } else {
      // eslint-disable-next-line no-console
      console.log('[API Config] Production mode, using API URL:', prodUrl);
    }
    return prodUrl;
  }

  // For web in development, ALWAYS use empty string so proxy handles the routing
  // This ensures requests go through the proxy which can reach the backend Docker container
  // eslint-disable-next-line no-console
  console.log('[API Config] Development mode, using proxy (empty baseURL)');
  return '';
};

const API_URL = getApiUrl();

// Log the final API URL configuration on startup
// eslint-disable-next-line no-console
console.log('[API Config] Final configuration:', {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  isNativePlatform: isNativePlatform(),
  finalApiUrl: API_URL,
});

// Strip query strings (may contain secrets such as reset tokens) before logging
const stripQuery = (url?: string): string | undefined => url?.split('?')[0];

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Temporarily disable credentials to test CORS
  timeout: 30000, // 30 second timeout to handle Cloud Run cold starts
});

// Add auth token to requests
api.interceptors.request.use((config: any) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Log outgoing requests for debugging (development only)
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log('[API Request]', config.method?.toUpperCase(), stripQuery(config.url), {
      baseURL: config.baseURL,
      fullURL: config.baseURL + stripQuery(config.url),
    });
  }
  return config;
});

// Handle auth errors and response validation
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Only check for HTML on specific endpoints that should return JSON
    if (response.config.url?.includes('/api/')) {
      if (typeof response.data === 'string' && response.data.includes('<!DOCTYPE html>')) {
        return Promise.reject(
          new Error('Invalid response format: API returned HTML instead of JSON')
        );
      }
    }
    return response;
  },
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      // Log detailed error information
      console.error('[API Error]', {
        url: stripQuery(error.config?.url),
        method: error.config?.method,
        baseURL: error.config?.baseURL,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        code: error.code,
        isNetworkError: error.message === 'Network Error',
        isTimeout: error.code === 'ECONNABORTED',
      });

      // Auth endpoints handle their own 401s (login errors, useAuth clears stale tokens,
      // ProtectedRoute redirects protected pages), so don't force a redirect for them.
      if (error.response?.status === 401 && !error.config?.url?.includes('/api/auth/')) {
        localStorage.removeItem('token');
        window.location.href = '/signin';
      }
    } else {
      console.error('[API Error] Non-Axios error:', error);
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (data: LoginData): Promise<Token> => {
    const formData = new FormData();
    formData.append('username', data.username);
    formData.append('password', data.password);

    const response = await api.post('/api/auth/login', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  register: async (data: UserCreate): Promise<Token> => {
    const response = await api.post('/api/auth/register', data);
    return response.data;
  },

  getMe: async (): Promise<User> => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  getUsers: async (): Promise<User[]> => {
    const response = await api.get('/api/auth/users');
    return response.data;
  },

  googleLogin: async (idToken: string): Promise<SocialAuthResponse> => {
    const response = await api.post('/api/auth/google', { firebase_token: idToken });
    return response.data;
  },

  facebookLogin: async (idToken: string): Promise<SocialAuthResponse> => {
    const response = await api.post('/api/auth/facebook', { firebase_token: idToken });
    return response.data;
  },

  updateProfile: async (profileData: Record<string, unknown>): Promise<unknown> => {
    // First check if provider profile exists
    try {
      await api.get('/api/providers/me');
      // If exists, update it
      const response = await api.put('/api/providers/me', profileData);
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // If not exists, create it
        const response = await api.post('/api/providers/', profileData);
        return response.data;
      }
      throw error;
    }
  },

  updateUserProfile: async (userData: { profile_image_url?: string }): Promise<User> => {
    const response = await api.put('/api/auth/me', userData);
    return response.data;
  },

  forgotPassword: async (email: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/api/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (
    token: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/api/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  verifyResetToken: async (token: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/api/auth/verify-reset-token?token=${token}`);
    return response.data;
  },

  requestEmailVerification: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/api/auth/verify-email/request');
    return response.data;
  },

  confirmEmailVerification: async (
    token: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/api/auth/verify-email/confirm', { token });
    return response.data;
  },
};

export default api;
